import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, User, Users, Lock, Unlock, Key, LogOut, Copy, X, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const StaffList = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [staffTypeFilter, setStaffTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active'); // Active, Blocked, Resigned, All
  const [loading, setLoading] = useState(false);
  const [credentialsModal, setCredentialsModal] = useState(null);
  const [resignModal, setResignModal] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/staff/all-with-blocked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      // Fallback to regular endpoint
      try {
        const response = await axios.get(`${API_URL}/api/staff/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStaff(response.data);
      } catch (err) {
        console.error('Error fetching staff:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (staffMember) => {
    navigate(`/hr/edit-staff/${staffMember.id}`, { state: { staffData: staffMember } });
  };

  const handleDelete = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to delete ${staffName}? This action cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff member deleted successfully!');
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete staff member');
    }
  };

  const handleBlock = async (staffId, staffName) => {
    if (!window.confirm(`Are you sure you want to block ${staffName}? They will not be able to login to Staff Portal.`)) {
      return;
    }
    try {
      await axios.put(`${API_URL}/api/staff/${staffId}/block`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${staffName} has been blocked`);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block staff');
    }
  };

  const handleUnblock = async (staffId, staffName) => {
    try {
      await axios.put(`${API_URL}/api/staff/${staffId}/unblock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${staffName} has been unblocked`);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unblock staff');
    }
  };

  const handleResetPassword = async (staffId, staffName) => {
    if (!window.confirm(`Reset password for ${staffName}? A new password will be generated.`)) {
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/staff/${staffId}/reset-password`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredentialsModal({
        title: 'Password Reset Successful',
        staffName: staffName,
        ...response.data.credentials
      });
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleResign = async () => {
    if (!resignModal) return;
    try {
      await axios.put(`${API_URL}/api/staff/${resignModal.id}/resign`, {
        exit_date: resignModal.exitDate,
        reason: resignModal.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${resignModal.name} has been marked as resigned`);
      setResignModal(null);
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process resignation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const filteredStaff = staff.filter(s => {
    // Filter by status
    if (statusFilter === 'Active' && (s.is_blocked || s.status === 'Resigned')) return false;
    if (statusFilter === 'Blocked' && !s.is_blocked) return false;
    if (statusFilter === 'Resigned' && s.status !== 'Resigned') return false;
    
    // Filter by staff type
    if (staffTypeFilter !== 'All') {
      const staffType = s.staff_type || s.role;
      if (staffType !== staffTypeFilter) return false;
    }
    
    // Filter by search term
    const searchLower = searchTerm.toLowerCase();
    return (
      (s.full_name || '').toLowerCase().includes(searchLower) ||
      (s.email || '').toLowerCase().includes(searchLower) ||
      (s.employee_id || '').toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (member) => {
    if (member.status === 'Resigned') {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Resigned</span>;
    }
    if (member.is_blocked) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Blocked</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff List</h1>
          <p className="text-sm text-gray-500 mt-1">Manage all staff members</p>
        </div>
        <button 
          onClick={() => navigate('/hr/add-staff')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add New Staff
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Total Staff</p>
          <p className="text-2xl font-bold text-gray-900">{staff.length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Teaching</p>
          <p className="text-2xl font-bold text-green-600">{staff.filter(s => (s.staff_type || s.role) === 'Teaching').length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Non-Teaching</p>
          <p className="text-2xl font-bold text-purple-600">{staff.filter(s => (s.staff_type || s.role) === 'Non-Teaching').length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-2xl font-bold text-blue-600">{staff.filter(s => !s.is_blocked && s.status !== 'Resigned').length}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm text-gray-500">Blocked/Resigned</p>
          <p className="text-2xl font-bold text-red-600">{staff.filter(s => s.is_blocked || s.status === 'Resigned').length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <input
            type="text"
            placeholder="Search by name, email or employee ID..."
            className="flex-1 px-4 py-2 border rounded-lg"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="flex gap-2 flex-wrap">
            {['All', 'Active', 'Blocked', 'Resigned'].map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === status
                    ? status === 'Blocked' ? 'bg-red-600 text-white' 
                    : status === 'Resigned' ? 'bg-gray-600 text-white'
                    : 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          {['All', 'Teaching', 'Non-Teaching'].map(type => (
            <button
              key={type}
              onClick={() => setStaffTypeFilter(type)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                staffTypeFilter === type
                  ? type === 'Teaching' ? 'bg-green-600 text-white' 
                  : type === 'Non-Teaching' ? 'bg-purple-600 text-white'
                  : 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : filteredStaff.length === 0 ? (
              <tr><td colSpan="8" className="px-4 py-8 text-center text-gray-500">No staff members found</td></tr>
            ) : (
              filteredStaff.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono text-blue-600">{member.employee_id}</td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{member.full_name}</p>
                      <p className="text-xs text-gray-500">{member.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`staff-contact-${member.id}`}>
                    <div>
                      {member.personal_email && member.personal_email !== member.email && (
                        <p className="text-xs text-gray-600">{member.personal_email}</p>
                      )}
                      <p className="text-sm text-gray-900">{member.phone || '—'}</p>
                      {member.whatsapp_no && member.whatsapp_no !== member.phone && (
                        <p className="text-xs text-green-600">WA: {member.whatsapp_no}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{member.staff_type || member.role}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{member.department || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{member.created_by_name || '-'}</td>
                  <td className="px-4 py-3 text-center">{getStatusBadge(member)}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-center gap-1">
                      <button 
                        onClick={() => handleEdit(member)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      
                      <button 
                        onClick={() => handleResetPassword(member.id, member.full_name)}
                        className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded"
                        title="Reset Password"
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      
                      {member.status !== 'Resigned' && (
                        member.is_blocked ? (
                          <button 
                            onClick={() => handleUnblock(member.id, member.full_name)}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                            title="Unblock"
                          >
                            <Unlock className="h-4 w-4" />
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleBlock(member.id, member.full_name)}
                            className="p-1.5 text-orange-600 hover:bg-orange-50 rounded"
                            title="Block"
                          >
                            <Lock className="h-4 w-4" />
                          </button>
                        )
                      )}
                      
                      {member.status !== 'Resigned' && !member.is_blocked && (
                        <button 
                          onClick={() => setResignModal({
                            id: member.id,
                            name: member.full_name,
                            exitDate: new Date().toISOString().split('T')[0],
                            reason: ''
                          })}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                          title="Resign"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                      )}
                      
                      <button 
                        onClick={() => handleDelete(member.id, member.full_name)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Credentials Modal */}
      {credentialsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">{credentialsModal.title}</h3>
              <button onClick={() => setCredentialsModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
              <p className="text-sm text-green-800 font-medium">{credentialsModal.staffName}</p>
              
              <div>
                <label className="text-xs text-gray-500">Username (Employee ID)</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                    {credentialsModal.username}
                  </code>
                  <button 
                    onClick={() => copyToClipboard(credentialsModal.username)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-gray-500">Password</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                    {showPassword ? credentialsModal.password : '••••••••••'}
                  </code>
                  <button 
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button 
                    onClick={() => copyToClipboard(credentialsModal.password)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <p className="text-xs text-amber-600 mt-2">
                ⚠️ Staff must change password on first login
              </p>
            </div>
            
            <button
              onClick={() => {
                copyToClipboard(`Username: ${credentialsModal.username}\nPassword: ${credentialsModal.password}`);
              }}
              className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Copy All Credentials
            </button>
          </div>
        </div>
      )}

      {/* Resign Modal */}
      {resignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Resign Staff</h3>
              <button onClick={() => setResignModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Mark <strong>{resignModal.name}</strong> as resigned. This will disable their login access.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exit Date</label>
                <input
                  type="date"
                  value={resignModal.exitDate}
                  onChange={(e) => setResignModal({...resignModal, exitDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (Optional)</label>
                <textarea
                  value={resignModal.reason}
                  onChange={(e) => setResignModal({...resignModal, reason: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                  placeholder="Resignation, Termination, etc."
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setResignModal(null)}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleResign}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirm Resignation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffList;
